import { useEffect } from "react";
import styled from "styled-components";
import { Toggle } from "rsuite";
import { GlassCard, SectionRibbon, colors } from "../../../../../ui";
import { SeverityAlert } from "../../../../../../appHelpers/appCommonSC";
import { DateTime } from "luxon";
import { EditAlertFC } from "../EditAlertFC";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

export const AlertSetFC = ({
  metadata,
  displayAlertConfigs,
  dataConfig,
  dataTable,
  handleUserEnabled,
  refresh,
}) => {
  const { id } = useParams();

  useEffect(() => {}, [displayAlertConfigs]);

  const renderCurrentStatus = (config) => {
    if (!dataTable) return null;

    if (
      metadata.he_tank_volume &&
      (config.field_name === "he_level_value" ||
        config.field_name === "he_level_1_value")
    ) {
      const percentage = (
        (parseFloat(dataTable[config?.field_name]) /
          parseFloat(metadata.he_tank_volume)) *
        100
      ).toFixed(2);
      const units = config?.threshold_units || "";

      if (
        config?.operator === "greater_than" &&
        percentage > parseFloat(config?.threshold)
      ) {
        return (
          <SeverityAlert severity={config?.severity}>
            {percentage} {units}
          </SeverityAlert>
        );
      } else if (
        config?.operator === "less_than" &&
        percentage < parseFloat(config?.threshold)
      ) {
        return (
          <SeverityAlert severity={config?.severity}>
            {percentage} {units}
          </SeverityAlert>
        );
      } else if (config?.operator === "delta_neg") {
        return null;
      } else {
        return (
          <SeverityAlert>
            {percentage} {units}
          </SeverityAlert>
        );
      }
    }

    if (
      config?.operator === "greater_than" &&
      parseFloat(dataTable[config?.field_name]) > parseFloat(config?.threshold)
    ) {
      return (
        <SeverityAlert severity={config?.severity}>
          {dataTable[config?.field_name]} {config?.threshold_units || ""}
        </SeverityAlert>
      );
    }

    if (
      config?.operator === "less_than" &&
      parseFloat(dataTable[config?.field_name]) < parseFloat(config?.threshold)
    ) {
      return (
        <SeverityAlert severity={config?.severity}>
          {dataTable[config?.field_name]} {config?.threshold_units || ""}
        </SeverityAlert>
      );
    }

    if (
      config?.operator === "equals" &&
      config.field_name === "comp_vib_status"
    ) {
      return dataTable[config?.field_name] === 0 ? (
        <SeverityAlert severity={config?.severity}>OFF</SeverityAlert>
      ) : (
        <SeverityAlert>ON</SeverityAlert>
      );
    }

    if (config?.operator === "offline" || config?.operator === "frozen_host") {
      const hoursDiff = Math.round(
        DateTime.now().diff(
          DateTime.fromISO(dataTable[config?.field_name]),
          "hours",
        ).hours,
      );
      const units = config?.threshold_units || "";

      if (hoursDiff > parseFloat(config?.threshold)) {
        return (
          <SeverityAlert severity={config?.severity}>
            {hoursDiff} {units}
          </SeverityAlert>
        );
      } else {
        return (
          <SeverityAlert>
            {hoursDiff} {units}
          </SeverityAlert>
        );
      }
    }

    if (
      config?.operator === "delta_neg" ||
      config?.operator === "absent_null_data"
    ) {
      return null;
    }

    return (
      <SeverityAlert>
        {dataTable[config?.field_name]} {config?.threshold_units || ""}
      </SeverityAlert>
    );
  };

  const renderMessage = (config) => {
    if (!dataTable) return null;

    const value = dataTable[config?.field_name];
    const threshold = config?.threshold;
    const units = config?.threshold_units || "";

    if (
      config?.operator === "greater_than" &&
      parseFloat(value) > parseFloat(threshold)
    ) {
      return `${value} ${units} is greater than ${threshold} ${units}`;
    }

    if (
      config?.operator === "less_than" &&
      parseFloat(value) < parseFloat(threshold)
    ) {
      return `${value} ${units} is less than ${threshold} ${units}`;
    }

    return null;
  };

  const renderAlertTrigger = (config) => {
    if (config.field_name === "comp_vib_status") {
      return "Trigger: Compressor is OFF!";
    }

    if (config.operator === "absent_null_data") {
      return `Trigger: There has been ${config?.threshold} count(s) of ${config?.field_name_alias}.`;
    }

    if (config?.operator === "offline" || config?.operator === "frozen_host") {
      return `Trigger: ${config?.field_name_alias} within past ${config?.threshold} ${config.threshold_units || ""}`;
    }

    return `Trigger: ${config?.field_name_alias} is ${config.operator?.replace(/_/g, " ")} ${config?.threshold} ${config.threshold_units || ""}`;
  };

  return (
    <ModernAlertContainer>
      <GlassCard animated visible={true}>
        {displayAlertConfigs && displayAlertConfigs?.length > 0 ? (
          <AlertsList>
            {displayAlertConfigs.map((config, index) =>
              config.user_id !== "default" ? (
                <AlertCard
                  key={config.id + "_set"}
                  $disabled={
                    !displayAlertConfigs.find(
                      (conf) =>
                        conf.user_id === "default" &&
                        conf.id === config.default_id,
                    )?.enabled
                  }
                >
                  {!displayAlertConfigs.find(
                    (conf) =>
                      conf.user_id === "default" &&
                      conf.id === config.default_id,
                  )?.enabled && (
                    <DisabledWarning>
                      <WarningIcon>⚠️</WarningIcon>
                      <WarningText>
                        This alert has been globally disabled!
                      </WarningText>
                    </DisabledWarning>
                  )}

                  <AlertContent>
                    <AlertHeader>
                      <SeverityBadge>
                        <SeverityAlert severity={config?.severity}>
                          {config?.severity}
                        </SeverityAlert>
                      </SeverityBadge>

                      <AlertDescription>
                        {renderAlertTrigger(config)}
                      </AlertDescription>
                    </AlertHeader>

                    <AlertDetails>
                      <DetailSection>
                        <DetailLabel>Current Status</DetailLabel>
                        <DetailValue>{renderCurrentStatus(config)}</DetailValue>
                      </DetailSection>

                      <DetailSection>
                        <DetailLabel>Message</DetailLabel>
                        <DetailValue>
                          {renderMessage(config) || "Normal"}
                        </DetailValue>
                      </DetailSection>

                      <DetailSection>
                        <DetailLabel>Frequency</DetailLabel>
                        <DetailValue>
                          <FrequencyText>
                            {config?.notify_frequency ||
                              displayAlertConfigs?.find(
                                (def) => def.id === config.default_id,
                              )?.notify_frequency}
                          </FrequencyText>
                          hours
                        </DetailValue>
                      </DetailSection>

                      <DetailSection>
                        <DetailLabel>Enabled</DetailLabel>
                        <DetailValue>
                          {displayAlertConfigs.find(
                            (conf) =>
                              conf.user_id === "default" &&
                              conf.id === config.default_id,
                          )?.enabled ? (
                            <Toggle
                              defaultChecked={config.enabled}
                              onClick={() => handleUserEnabled(config.id)}
                            />
                          ) : (
                            <Toggle defaultChecked={config.enabled} disabled />
                          )}
                        </DetailValue>
                      </DetailSection>

                      <DetailSection>
                        <DetailLabel>Actions</DetailLabel>
                        <DetailValue>
                          {dataTable && (
                            <EditAlertFC
                              config={config}
                              dataConfig={dataConfig}
                              dataTable={dataTable}
                              sme={id}
                              refresh={refresh}
                            />
                          )}
                        </DetailValue>
                      </DetailSection>
                    </AlertDetails>
                  </AlertContent>
                </AlertCard>
              ) : null,
            )}
          </AlertsList>
        ) : (
          <NoAlertsMessage>
            <NoAlertsIcon>🔔</NoAlertsIcon>
            <NoAlertsText>No Alerts Configured</NoAlertsText>
          </NoAlertsMessage>
        )}
      </GlassCard>
    </ModernAlertContainer>
  );
};

// Modern Styled Components with Glass Morphism
const ModernAlertContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const AlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const AlertCard = styled.div`
  background: ${(props) =>
    props.$disabled
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.3))`
      : `linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))`};
  border: 2px solid
    ${(props) =>
      props.$disabled ? "rgba(0, 0, 0, 0.08)" : "rgba(0, 0, 0, 0.12)"};
  border-radius: 12px;
  padding: 12px 16px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.$disabled ? 0.7 : 1)};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:hover {
    background: ${(props) =>
      props.$disabled
        ? `linear-gradient(135deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.4))`
        : `linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))`};
    border-color: ${colors.primary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const DisabledWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding: 8px 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.15),
    rgba(255, 193, 7, 0.05)
  );
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
`;

const WarningIcon = styled.span`
  font-size: 16px;
`;

const WarningText = styled.span`
  color: ${colors.neutral[800]};
  font-size: 14px;
  font-weight: 500;
`;

const AlertContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 4px;
`;

const SeverityBadge = styled.div`
  flex-shrink: 0;
`;

const AlertDescription = styled.div`
  flex: 1;
  color: ${colors.neutral[800]};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
`;

const AlertDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 4px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.div`
  color: ${colors.neutral[600]};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.div`
  color: ${colors.neutral[800]};
  font-size: 13px;
  font-weight: 500;
  min-height: 20px;
  display: flex;
  align-items: center;
`;

const FrequencyText = styled.span`
  font-weight: 600;
  color: ${colors.primary.main};
  margin-right: 4px;
`;

const NoAlertsMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  text-align: center;
`;

const NoAlertsIcon = styled.div`
  font-size: 2rem;
  opacity: 0.5;
`;

const NoAlertsText = styled.div`
  color: ${colors.neutral[600]};
  font-size: 16px;
  font-weight: 500;
`;
